import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Tag,
  Alert,
} from 'antd';
import './settings-modal.scss';

const { Option } = Select;

class AddTaskRoutingRuleModal extends Component {
  constructor(props) {
    super(props);
    const { editingRule } = props;

    // Use labels array if available, fall back to single label for backwards compatibility
    const existingLabelIds = editingRule?.labels?.length > 0
      ? editingRule.labels.map((l) => l.id)
      : (editingRule?.label?.id ? [editingRule.label.id] : []);

    this.state = {
      name: editingRule?.name || '',
      documentCategoryId: editingRule?.document_category?.id || null,
      labelIds: existingLabelIds,
      labelMatchMode: editingRule?.label_match_mode || 'any', // 'any' (OR) or 'all' (AND)
      taskTemplateId: editingRule?.action_id || null,
      active: editingRule?.active ?? true,
      noLabel: existingLabelIds.length === 0 && (!editingRule || !editingRule.label),
      // Track if fields have been touched (edited then cleared) to show validation
      categoryTouched: false,
      templateTouched: false,
      labelsTouched: false,
    };
  }

  handleSubmit = () => {
    const {
      name,
      documentCategoryId,
      labelIds,
      labelMatchMode,
      taskTemplateId,
      active,
      noLabel,
    } = this.state;
    const { onSave, editingRule } = this.props;

    const validLabelIds = labelIds.filter((id) => id != null);
    const conditionAttributes = editingRule
      ? this.buildUpdateConditions(documentCategoryId, validLabelIds, noLabel)
      : this.buildCreateConditions(documentCategoryId, validLabelIds, noLabel);

    onSave({
      name: name || null,
      action_type: 'EmaTaskTemplate',
      action_id: taskTemplateId,
      active,
      label_match_mode: validLabelIds.length > 1 ? labelMatchMode : 'any',
      task_routing_conditions_attributes: conditionAttributes,
    });
  };

  buildCreateConditions = (documentCategoryId, labelIds, noLabel) => {
    const conditions = [
      { kind: 'document_category', operator: 'equals', value: documentCategoryId },
    ];

    if (!noLabel && labelIds.length > 0) {
      labelIds.forEach((labelId) => {
        conditions.push({ kind: 'label', operator: 'equals', value: labelId });
      });
    }

    return conditions;
  };

  buildUpdateConditions = (documentCategoryId, labelIds, noLabel) => {
    const { editingRule } = this.props;
    const existingCategoryCondition = editingRule.conditions?.find(
      (c) => c.kind === 'document_category'
    );
    const existingLabelConditions = editingRule.conditions?.filter(
      (c) => c.kind === 'label'
    ) || [];

    const conditions = [
      {
        id: existingCategoryCondition?.id,
        kind: 'document_category',
        operator: 'equals',
        value: documentCategoryId,
        _destroy: false,
      },
    ];

    if (noLabel || labelIds.length === 0) {
      // Mark all existing label conditions for deletion
      existingLabelConditions.forEach((c) => {
        conditions.push({ id: c.id, _destroy: true });
      });
    } else {
      // Match new label IDs with existing conditions where possible
      const usedExistingIds = new Set();

      labelIds.forEach((labelId, index) => {
        const existing = existingLabelConditions[index];
        if (existing) {
          usedExistingIds.add(existing.id);
          conditions.push({
            id: existing.id,
            kind: 'label',
            operator: 'equals',
            value: labelId,
            _destroy: false,
          });
        } else {
          conditions.push({ kind: 'label', operator: 'equals', value: labelId });
        }
      });

      // Mark unused existing label conditions for deletion
      existingLabelConditions.forEach((c) => {
        if (!usedExistingIds.has(c.id)) {
          conditions.push({ id: c.id, _destroy: true });
        }
      });
    }

    return conditions;
  };

  checkDuplicateWarning = () => {
    const { documentCategoryId, labelIds, noLabel } = this.state;
    const { existingRules, editingRule } = this.props;

    if (!documentCategoryId) return null;

    const warnings = [];

    if (noLabel) {
      // Check for duplicate "no label" rule
      const duplicate = existingRules.find((rule) => {
        if (editingRule && rule.id === editingRule.id) return false;
        const ruleCategoryId = rule.document_category?.id;
        const hasNoLabels = !rule.labels || rule.labels.length === 0;
        return ruleCategoryId === documentCategoryId && hasNoLabels;
      });

      if (duplicate) {
        return `A rule with the same category and no label already exists ("${duplicate.name || 'Unnamed'}"). The lower-priority rule will never be reached.`;
      }
    } else {
      // Check each selected label for overlaps with existing rules
      labelIds.forEach((labelId) => {
        const duplicate = existingRules.find((rule) => {
          if (editingRule && rule.id === editingRule.id) return false;
          const ruleCategoryId = rule.document_category?.id;
          const ruleLabelIds = rule.labels?.map((l) => l.id) || (rule.label?.id ? [rule.label.id] : []);
          return ruleCategoryId === documentCategoryId && ruleLabelIds.includes(labelId);
        });

        if (duplicate) {
          warnings.push(`"${duplicate.name || 'Unnamed'}"`);
        }
      });
    }

    if (warnings.length > 0) {
      // Deduplicate warnings (same rule can match multiple selected labels)
      const uniqueWarnings = [...new Set(warnings)];
      return `Rules with overlapping category and label combinations exist: ${uniqueWarnings.join(', ')}. The lower-priority rules may not be reached for those labels.`;
    }

    return null;
  };

  isFormValid = () => {
    const { documentCategoryId, taskTemplateId, noLabel, labelIds } = this.state;
    // Require labels when noLabel switch is OFF (user intends to match specific labels)
    const labelsValid = noLabel || labelIds.length > 0;
    return documentCategoryId && taskTemplateId && labelsValid;
  };

  render() {
    const {
      visible,
      onClose,
      categories,
      labels,
      taskTemplates,
      editingRule,
      isLoading,
    } = this.props;

    const {
      name,
      documentCategoryId,
      labelIds,
      labelMatchMode,
      taskTemplateId,
      active,
      noLabel,
      categoryTouched,
      templateTouched,
      labelsTouched,
    } = this.state;

    const duplicateWarning = this.checkDuplicateWarning();

    // Only show validation errors if field was touched and then cleared
    const showCategoryError = categoryTouched && !documentCategoryId;
    const showTemplateError = templateTouched && !taskTemplateId;
    const showLabelsError = labelsTouched && !noLabel && labelIds.length === 0;

    return (
      <Modal
        wrapClassName="settings-modal-wrap"
        visible={visible}
        title={editingRule ? 'Edit Routing Rule' : 'Add Routing Rule'}
        onCancel={onClose}
        onOk={this.handleSubmit}
        okText={editingRule ? 'Save' : 'Create'}
        okButtonProps={{
          disabled: !this.isFormValid(),
          loading: isLoading,
        }}
        width={500}
      >
        <Alert
          type="info"
          showIcon
          message="When are these rules used?"
          description={(
            <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
              <li>Rules trigger when a fax is auto-uploaded to EMA with a matching document category</li>
              <li>The template&apos;s owner (Department/User) must match the fax number owner</li>
              <li>
                <strong>Example:</strong>
                {' '}
                A &quot;Prior Auth&quot; fax with label &quot;Dr. Smith&quot; routes to the &quot;PA - Smith Team&quot; task template
              </li>
            </ul>
          )}
          style={{ marginBottom: 16 }}
        />

        {duplicateWarning && (
          <Alert
            type="warning"
            showIcon
            message={duplicateWarning}
            style={{ marginBottom: 16 }}
          />
        )}

        <Form layout="vertical">
          <Form.Item
            label="Name"
            help="Optional - helps distinguish similar rules"
          >
            <Input
              value={name}
              onChange={(e) => this.setState({ name: e.target.value })}
              placeholder="e.g., Dr. Smith Prior Auths"
            />
          </Form.Item>

          <Form.Item
            label="Document Category"
            required
            validateStatus={showCategoryError ? 'error' : ''}
            help={showCategoryError ? 'Please select a document category' : ''}
          >
            <Select
              value={documentCategoryId}
              onChange={(value) => this.setState({
                documentCategoryId: value,
                categoryTouched: true,
              })}
              placeholder="Select a document category"
              showSearch
              optionFilterProp="children"
            >
              {categories.map((cat) => (
                <Option key={cat.id} value={cat.id}>
                  {cat.emr_category}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Label"
            validateStatus={showLabelsError ? 'error' : ''}
            help={showLabelsError
              ? 'Please select at least one label, or enable "No label" for a catch-all rule'
              : (labelIds.length > 1
                ? (labelMatchMode === 'all'
                  ? `Rule will match documents with ALL of these ${labelIds.length} labels`
                  : `Rule will match documents with ANY of these ${labelIds.length} labels`)
                : '')}
          >
            <div style={{ marginBottom: 8 }}>
              <Switch
                checked={noLabel}
                onChange={(checked) =>
                  this.setState({ noLabel: checked, labelIds: [], labelsTouched: true })
                }
                size="small"
              />
              <span style={{ marginLeft: 8 }}>
                No label (default) - matches any label
              </span>
            </div>
            {!noLabel && (
              <>
                <Select
                  mode="multiple"
                  value={labelIds}
                  onChange={(value) => {
                    // Value is an array (or undefined/null when cleared)
                    // Filter out any falsy values to handle edge cases
                    const newLabelIds = Array.isArray(value)
                      ? value.filter((v) => v != null)
                      : (value ? [value] : []);
                    this.setState({ labelIds: newLabelIds, labelsTouched: true });
                  }}
                  placeholder="Select one or more labels"
                  showSearch
                  optionFilterProp="label"
                  allowClear
                >
                  {labels.map((label) => {
                    const textColor = label.is_dark_text ? 'black' : 'white';
                    return (
                      <Option key={label.id} value={label.id} label={label.name}>
                        <Tag
                          color={label.color}
                          style={{ color: textColor, marginRight: 8 }}
                        >
                          {label.name}
                        </Tag>
                      </Option>
                    );
                  })}
                </Select>
                {labelIds.length > 1 && (
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: '#666' }}>Match:</span>
                    <Select
                      value={labelMatchMode}
                      onChange={(value) => this.setState({ labelMatchMode: value })}
                      style={{ width: 280 }}
                      size="small"
                    >
                      <Option value="any">
                        <strong>any</strong>
                        {' '}
                        <span style={{ color: '#888' }}>(document has any label)</span>
                      </Option>
                      <Option value="all">
                        <strong>all</strong>
                        {' '}
                        <span style={{ color: '#888' }}>(document has all labels)</span>
                      </Option>
                    </Select>
                  </div>
                )}
              </>
            )}
          </Form.Item>

          <Form.Item
            label="Task Template"
            required
            validateStatus={showTemplateError ? 'error' : ''}
            help={showTemplateError ? 'Please select a task template' : ''}
          >
            <Select
              value={taskTemplateId}
              onChange={(value) => this.setState({
                taskTemplateId: value,
                templateTouched: true,
              })}
              placeholder="Select a task template"
              showSearch
              optionFilterProp="children"
            >
              {taskTemplates.map((template) => (
                <Option key={template.id} value={template.id}>
                  {template.task_name}
                  {template.owner_type && (
                    <span style={{ color: '#888', marginLeft: 8 }}>
                      ({template.owner_type})
                    </span>
                  )}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Active">
            <Switch
              checked={active}
              onChange={(checked) => this.setState({ active: checked })}
            />
            <span style={{ marginLeft: 8 }}>
              {active ? 'Rule is active' : 'Rule is inactive'}
            </span>
          </Form.Item>
        </Form>
      </Modal>
    );
  }
}

AddTaskRoutingRuleModal.defaultProps = {
  editingRule: null,
  categories: [],
  labels: [],
  taskTemplates: [],
  existingRules: [],
  isLoading: false,
};

AddTaskRoutingRuleModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  editingRule: PropTypes.object,
  categories: PropTypes.array,
  labels: PropTypes.array,
  taskTemplates: PropTypes.array,
  existingRules: PropTypes.array,
  isLoading: PropTypes.bool,
};

export default AddTaskRoutingRuleModal;
